import React, { useState } from "react";
import {
  Dialog, DialogTitle, DialogContent, IconButton, Grid,
  Typography, Button, TextField, Box, Divider, Chip, Slide
} from "@mui/material";
import {
  Close as CloseIcon, PictureAsPdf as PdfIcon,
  Gavel as InvestigateIcon, Groups as CommitteeIcon,
  CheckCircle as ResolveIcon, Cancel as RejectIcon
} from "@mui/icons-material";
import { putData } from "../../BaseUrl";

const Transition = React.forwardRef(function Transition(props, ref) {
  return <Slide direction="up" ref={ref} {...props} />;
});

const Field = ({ label, value }) => (
  <Box mb={1.5}>
    <Typography variant="caption" color="text.secondary">{label}</Typography>
    <Typography variant="body2" fontWeight={500}>{value || "-"}</Typography>
  </Box>
);

const ComplaintDetailDialog = ({ open, onClose, complaint, onSuccess }) => {
  const [remarks, setRemarks] = useState("");
  const [rejectReason, setRejectReason] = useState("");
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState("");

  if (!complaint) return null;

  const handleUpdateStatus = async (newStatus, payload = {}) => {
    setLoading(true);
    setError("");
    try {
      await putData(`admin/complaint/update-status/${complaint.id}`, {
        status: newStatus,
        ...payload
      });
      onSuccess(`Complaint marked as ${newStatus}`);
    } catch (err) {
      setError(err?.response?.data?.message || "Update failed");
    }
    setLoading(false);
  };

  const handleClose = () => {
    setRemarks("");
    setRejectReason("");
    setError("");
    onClose();
  };

  const statusColorMap = {
    "Assigned to Committee": { color: "#336599", bg: "#f3e5f5" },
    "Under Investigation": { color: "#ff9800", bg: "#fff3e0" },
    "New": { color: "#f44336", bg: "#fce4ec" },
    "Resolved": { color: "#4caf50", bg: "#e8f5e9" },
    "Rejected": { color: "#616161", bg: "#f5f5f5" }
  };
  const st = statusColorMap[complaint.status] || statusColorMap["New"];

  return (
    <Dialog open={open} onClose={handleClose} maxWidth="md" fullWidth TransitionComponent={Transition}>
      <DialogTitle sx={{ display: "flex", justifyContent: "space-between", alignItems: "center" }}>
        <Box>
          <Typography variant="h6" fontWeight="bold">Complaint Details</Typography>
          <Typography variant="body2" color="text.secondary">
            #{complaint.complaintId || complaint.id}
          </Typography>
        </Box>
        <IconButton onClick={handleClose}><CloseIcon /></IconButton>
      </DialogTitle>

      <DialogContent dividers>
        <Grid container spacing={3}>
          {/* Left Panel - Complainant Info */}
          <Grid item xs={12} md={6}>
            <Typography variant="subtitle1" fontWeight="bold" color="#1a237e" gutterBottom>
              Complainant Information
            </Typography>
            <Field label="Name" value={complaint.complainant || complaint.complainantName} />
            <Field label="Date Filed" value={complaint.date || complaint.filedDate || complaint.createdAt?.slice(0, 10)} />
            <Field label="Subject" value={complaint.subject || complaint.title} />
            <Field label="Committee" value={complaint.committee || complaint.assignedCommittee} />
            <Box mb={1.5}>
              <Typography variant="caption" color="text.secondary">Status</Typography>
              <Box mt={0.5}>
                <Chip label={complaint.status || "New"} size="small"
                  sx={{ backgroundColor: st.bg, color: st.color, fontWeight: 600 }} />
              </Box>
            </Box>
            <Field label="Description" value={complaint.description || complaint.details} />

            <Typography variant="caption" color="text.secondary">Remarks</Typography>
            <TextField fullWidth size="small" multiline rows={3}
              placeholder="Add remarks..."
              value={remarks} onChange={(e) => setRemarks(e.target.value)}
              sx={{ mt: 0.5 }} />
          </Grid>

          {/* Right Panel - Attachment */}
          <Grid item xs={12} md={6}>
            <Typography variant="subtitle1" fontWeight="bold" color="#1a237e" gutterBottom>
              Attachment
            </Typography>
            {complaint.attachment || complaint.attachmentUrl ? (
              <Box sx={{
                p: 2, borderRadius: 2, border: "1px solid #e0e0e0",
                display: "flex", alignItems: "center", gap: 2, backgroundColor: "#fafafa"
              }}>
                <PdfIcon sx={{ fontSize: 40, color: "#f44336" }} />
                <Box>
                  <Typography variant="body2" fontWeight={500}>
                    {complaint.attachmentName || "complaint_document.pdf"}
                  </Typography>
                  <Typography variant="caption" color="text.secondary">
                    {complaint.attachmentSize || "245 KB"} | {complaint.attachmentDate || "-"}
                  </Typography>
                </Box>
              </Box>
            ) : (
              <Typography variant="body2" color="text.secondary">No attachment</Typography>
            )}

            <Divider sx={{ my: 3 }} />

            {/* Start Investigation */}
            <Typography variant="subtitle1" fontWeight="bold" color="#1a237e" gutterBottom>
              Start Investigation
            </Typography>
            <Box sx={{ display: "flex", gap: 1, mb: 3 }}>
              <Button variant="contained" startIcon={<InvestigateIcon />} disabled={loading}
                onClick={() => handleUpdateStatus("Under Investigation", { remarks })}
                sx={{ backgroundColor: "#2196f3", textTransform: "none", flex: 1,
                  "&:hover": { backgroundColor: "#1976d2" } }}>
                Mark as Under Investigation
              </Button>
              <Button variant="outlined" startIcon={<CommitteeIcon />} disabled={loading}
                onClick={() => handleUpdateStatus("Assigned to Committee", { remarks })}
                sx={{ textTransform: "none", flex: 1 }}>
                Assign Committee
              </Button>
            </Box>

            <Divider sx={{ my: 2 }} />

            {/* Close Complaint */}
            <Typography variant="subtitle1" fontWeight="bold" color="#1a237e" gutterBottom>
              Close Complaint
            </Typography>
            <Button variant="contained" fullWidth startIcon={<ResolveIcon />} disabled={loading}
              onClick={() => handleUpdateStatus("Resolved", { remarks })}
              sx={{ backgroundColor: "#4caf50", textTransform: "none", mb: 2,
                "&:hover": { backgroundColor: "#388e3c" } }}>
              Mark as Resolved
            </Button>
            <TextField fullWidth size="small" multiline rows={2}
              placeholder="Reason for rejection..."
              value={rejectReason} onChange={(e) => setRejectReason(e.target.value)}
              sx={{ mb: 1 }} />
            <Button variant="contained" fullWidth startIcon={<RejectIcon />} disabled={loading || !rejectReason.trim()}
              onClick={() => handleUpdateStatus("Rejected", { remarks, rejectReason })}
              sx={{ backgroundColor: "#f44336", textTransform: "none",
                "&:hover": { backgroundColor: "#d32f2f" } }}>
              Reject Complaint
            </Button>
          </Grid>
        </Grid>

        {error && <Typography color="error" variant="body2" mt={2}>{error}</Typography>}
      </DialogContent>
    </Dialog>
  );
};

export default ComplaintDetailDialog;
